import { useScrollRevealGroup } from '../../../hooks/useScrollReveal';

const tactics = [
  {
    icon: 'ri-percent-line',
    stat: '+40%',
    title: 'Statistics & Data',
    desc: 'Content containing specific, credible stats sees up to 40% higher AI citation rates. Numbers signal authority and precision — exactly what AI systems trust.',
  },
  {
    icon: 'ri-double-quotes-r',
    stat: '+40%',
    title: 'Quotations & Expert Voices',
    desc: 'Direct quotes from real people or authorities boost citation likelihood by up to 40%. AI systems treat attributed expertise as a credibility signal.',
  },
  {
    icon: 'ri-links-line',
    stat: '+30%',
    title: 'Cited Sources Within Content',
    desc: 'Linking out to reputable references signals trustworthiness to AI systems, increasing citation rates by up to 30%. Outbound authority is a trust signal, not a leak.',
  },
  {
    icon: 'ri-list-check-3',
    stat: 'High',
    title: 'Clear Structure & Headings',
    desc: 'AI systems extract information far more effectively from well-structured content with clear question-answering headings. Formatting is strategy.',
  },
  {
    icon: 'ri-cursor-line',
    stat: 'Critical',
    title: 'Direct Answers Early',
    desc: 'Content that answers the question in the first paragraph is more likely to be selected over content that buries the answer. Lead with the answer.',
  },
  {
    icon: 'ri-building-line',
    stat: 'Foundation',
    title: 'Consistent Brand Signals',
    desc: 'Reviews, directory listings, third-party mentions, and social proof all contribute to how AI systems assess your authority — across every platform.',
  },
];

export default function WhatMakesAICiteSection() {
  const groupRef = useScrollRevealGroup();

  return (
    <section id="what-makes-ai-cite" className="relative pt-24 pb-16 bg-ds-bg overflow-hidden">
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-ds-red to-transparent" />
      <div className="absolute inset-0 hero-grid opacity-10 pointer-events-none" />
      <div className="absolute top-1/2 right-0 w-[600px] h-[600px] bg-ds-red/5 blur-[150px] rounded-full pointer-events-none -translate-y-1/2" />

      <div className="relative max-w-7xl mx-auto px-6" ref={groupRef}>

        {/* Header */}
        <div className="mb-16">
          <span className="section-label reveal">Princeton University Research — 10,000+ Queries</span>
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8">
            <h2 className="font-display text-6xl md:text-7xl text-white leading-none reveal">
              WHAT MAKES AI
              <br />
              <span className="text-ds-red neon-text-red">CITE</span>
              <br />
              A BUSINESS.
            </h2>
            <p className="font-body text-white/50 text-sm leading-relaxed max-w-sm reveal">
              Princeton University research testing over 10,000 queries identified the highest-impact GEO tactics. These aren&apos;t guesses — they&apos;re the specific signals that move the needle.
            </p>
          </div>
          <div className="mt-8 h-px w-24 bg-ds-red reveal" style={{ boxShadow: '0 0 12px rgba(180,255,57,0.5)' }} />
        </div>

        {/* Tactics grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-ds-border/30 mb-12 reveal">
          {tactics.map((t) => (
            <div key={t.title} className="bg-ds-bg p-8 group hover:bg-ds-bg-2 transition-colors duration-300 relative overflow-hidden">
              {/* Stat watermark */}
              <div className="absolute top-4 right-5 font-display text-4xl text-ds-red/10 leading-none pointer-events-none select-none">
                {t.stat}
              </div>
              {/* Icon */}
              <div className="w-10 h-10 flex items-center justify-center border border-ds-border group-hover:border-ds-red/50 transition-colors duration-300 mb-5">
                <i className={`${t.icon} text-white/40 group-hover:text-ds-red text-lg transition-colors duration-300`} />
              </div>
              {/* Stat badge */}
              <div className="inline-flex items-center gap-1.5 border border-ds-red/30 bg-ds-red/8 px-2.5 py-1 mb-3">
                <span className="font-display text-ds-red text-xs tracking-widest">{t.stat}</span>
                <span className="font-body text-white/30 text-xs">citation lift</span>
              </div>
              <h3 className="font-display text-white text-base tracking-wide leading-tight mb-3">{t.title}</h3>
              <div className="h-px bg-ds-border mb-3 group-hover:bg-ds-red/20 transition-colors duration-300" />
              <p className="font-body text-white/55 text-xs leading-relaxed">{t.desc}</p>
            </div>
          ))}
        </div>

        {/* Manifesto strip */}
        <div className="border border-ds-red/30 bg-ds-bg-2 p-8 flex flex-col md:flex-row items-center gap-6 reveal" style={{ boxShadow: '0 0 40px rgba(180,255,57,0.06)', borderLeft: '3px solid #B4FF39' }}>
          <div className="w-12 h-12 flex items-center justify-center border border-ds-red/40 flex-shrink-0">
            <i className="ri-lightbulb-flash-line text-ds-red text-xl" />
          </div>
          <div>
            <p className="font-display text-lg md:text-xl text-white leading-tight">
              AI doesn&apos;t reward volume. It rewards{' '}
              <span className="text-ds-red">clarity, credibility, and structure.</span>
            </p>
            <p className="font-body text-white/50 text-sm mt-2 leading-relaxed">
              A focused, well-cited page beats a bloated 3,000-word article every time. This is the core principle behind everything we build for you.
            </p>
          </div>
        </div>

      </div>
    </section>
  );
}
